import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne focus:border-transparent";

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-6" data-testid="reset-page">
      <SEO title="Reset Password" description="Set a new password" />
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-serif text-3xl text-espresso">people, redefined.</Link>
        <form onSubmit={submit} data-testid="reset-form" className="bg-taupe rounded-2xl p-8 mt-8 space-y-5">
          <h1 className="font-serif text-2xl text-espresso">Set a new password</h1>
          {!token && <p className="text-red-700 text-sm">Missing or invalid reset link.</p>}
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">New password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-espresso mb-2">Confirm password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="reset-confirm" />
          </div>
          {error && <p className="text-red-700 text-sm" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={loading || !token} data-testid="reset-submit" className="w-full inline-flex items-center justify-center gap-2 bg-espresso text-ivory rounded-full px-6 py-3 font-medium hover:bg-forest transition-colors disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} Reset Password
          </button>
          <div className="text-center">
            <Link to="/login" className="text-sm text-espresso/60 hover:text-espresso transition-colors">Back to sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
